import type { QueryClient } from '@tanstack/react-query';

import type { AppShellApiClient } from '../shared/types';
import createQueryConfig, { NotFoundError } from '../queries';
import {
  parseAppointmentUsage,
  type AppointmentUsage,
} from '../features/bookings-upgrade/usage';
import createDebug from './debug';

const debug = createDebug('fetch-appointment-usage');

/**
 * Appointment allowance and spend for the current merchant, or `null` when the
 * merchant has none.
 *
 * Unlike `fetchSubscription`, this never rejects. It runs during bootstrap and
 * everything it feeds is an upsell: a 404 (not a WSB merchant), a rejected
 * token and a changed response shape all mean the same thing here — no card, no
 * dialog — and none of them is a reason to fail the shell's startup.
 */
export async function fetchAppointmentUsage(
  merchantId: string,
  apiClient: AppShellApiClient,
  queryClient: QueryClient
): Promise<AppointmentUsage | null> {
  try {
    const queryConfig = createQueryConfig(queryClient, apiClient);
    const { queryKey, queryFn } = queryConfig.appointmentUsage({ merchantId });

    const response = await queryClient.fetchQuery({ queryKey, queryFn });

    return parseAppointmentUsage(response);
  } catch (error) {
    // A 404 is the documented answer for a merchant with no allowance, not a
    // fault, so it is not worth a line even with debug logging switched on.
    if (error instanceof NotFoundError) return null;

    // Message only, never the error object: every authenticated query attaches
    // its bearer token to the rejected error (see injectErrorMetadata in
    // ../queries), and `debug` writes through console, where anything logged
    // can be picked up as a Sentry breadcrumb.
    debug(
      error instanceof Error ? `${error.name}: ${error.message}` : String(error)
    );
    return null;
  }
}
